import api from './api'

export interface ExamResultEmployee {
  user_id: number
  email: string
  user_type: number
  user_type_label: string
  user_status: number
  user_status_label: string
  user_details: {
    first_name: string
    middle_name?: string
    last_name: string
    full_name: string
  } | null
}

export interface ExamResultExam {
  exam_id: number
  exam_name: string
  passing_rate: number
  total_attempts?: number | null
  exam_content?: {
    questions: {
      id: number
      question: string
      type: 'multiple_choice' | 'true_false' | 'essay'
      options?: { id: string; text: string }[]
    }[]
  }
  answer_key?: Record<number, any>
  lesson: {
    lesson_id: number
    name: string
    description?: string
  } | null
}

export interface ExamResult {
  exam_result_id: number
  user_score: number
  total_score: number
  percentage_score: number
  remarks: number
  remarks_label: string
  num_of_attempts: number
  date_taken: string
  date_taken_formatted: string
  user_data_content?: any
  employee: ExamResultEmployee
  exam: ExamResultExam
}

export interface ExamResultsResponse {
  success: boolean
  message: string
  data: ExamResult[] | {
    current_page: number
    data: ExamResult[]
    first_page_url: string
    from: number
    last_page: number
    last_page_url: string
    links: Array<{
      url: string | null
      label: string
      active: boolean
    }>
    next_page_url: string | null
    path: string
    per_page: number
    prev_page_url: string | null
    to: number
    total: number
  }
}

export interface ExamResultResponse {
  success: boolean
  message: string
  data: ExamResult
}

export interface ExamResultFilters {
  employee_id?: number
  exam_id?: number
  lesson_id?: number
  remarks?: number | string
  date_from?: string
  date_to?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  per_page?: number
  page?: number
}

export interface ExamAttemptHistory {
  history_id: number
  original_exam_result_id: number
  exam_id: number
  user_id: number
  user_score: number
  total_score: number
  percentage_score: number
  remarks: number
  status: string
  num_of_attempts: number
  date_taken: string | null
  date_taken_formatted: string | null
  reset_date: string
  reset_date_formatted: string
  reset_reason: string
  employee?: {
    user_id: number
    email: string
    user_details?: {
      first_name: string
      last_name: string
      full_name: string
    } | null
  } | null
  reset_by?: {
    user_id: number
    name: string
    email: string
  } | null
  exam: {
    exam_id: number
    exam_name: string
    passing_rate: number
    lesson: {
      lesson_id: number
      name: string
    } | null
  }
}

export interface ExamAttemptHistoryResponse {
  success: boolean
  message: string
  employee?: {
    user_id: number
    name: string
    email: string
  }
  data: ExamAttemptHistory[]
  pagination: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
  timestamp: string
}

class ExamResultService {
  /**
   * Get all exam results with optional filters and pagination
   */
  async getAllExamResults(filters?: ExamResultFilters): Promise<ExamResultsResponse> {
    const params = new URLSearchParams()
    
    if (filters?.employee_id) params.append('employee_id', filters.employee_id.toString())
    if (filters?.exam_id) params.append('exam_id', filters.exam_id.toString())
    if (filters?.lesson_id) params.append('lesson_id', filters.lesson_id.toString())
    if (filters?.remarks !== undefined && filters.remarks !== '') {
      params.append('remarks', filters.remarks.toString())
    }
    if (filters?.date_from) params.append('date_from', filters.date_from)
    if (filters?.date_to) params.append('date_to', filters.date_to)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())
    if (filters?.page) params.append('page', filters.page.toString())

    const queryString = params.toString()
    const url = queryString ? `/exam-results?${queryString}` : '/exam-results'
    
    const response = await api.get<ExamResultsResponse>(url)
    return response.data
  }

  /**
   * Get exam results for the authenticated employee
   */
  async getMyEmployeeExamResults(filters?: Omit<ExamResultFilters, 'employee_id'>): Promise<ExamResultsResponse> {
    const params = new URLSearchParams()
    
    if (filters?.exam_id) params.append('exam_id', filters.exam_id.toString())
    if (filters?.lesson_id) params.append('lesson_id', filters.lesson_id.toString())
    if (filters?.remarks !== undefined && filters.remarks !== '') {
      params.append('remarks', filters.remarks.toString())
    }
    if (filters?.date_from) params.append('date_from', filters.date_from)
    if (filters?.date_to) params.append('date_to', filters.date_to)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)

    const queryString = params.toString()
    const url = queryString ? `/exams/my-results?${queryString}` : '/exams/my-results'
    
    const response = await api.get<ExamResultsResponse>(url)
    return response.data
  }

  /**
   * Get a specific exam result by ID (admin only)
   */
  async getExamResult(id: number): Promise<ExamResultResponse> {
    const response = await api.get<ExamResultResponse>(`/exam-results/${id}`)
    return response.data
  }

  /**
   * Get a specific exam result for the authenticated employee (employee only)
   * Uses the existing getExamResultDetail endpoint
   */
  async getMyExamResult(examResultId: number): Promise<ExamResultResponse> {
    // First, get the exam_id from the exam result
    // We'll need to fetch all results and find the one we need, or modify the approach
    // For now, we'll use the exam result ID to construct the call
    const response = await api.get<ExamResultResponse>(`/exam-results/${examResultId}`)
    return response.data
  }

  /**
   * Get detailed exam result using exam ID and result ID
   * This is the proper way to get exam result details
   */
  async getExamResultDetail(examId: number, resultId: number): Promise<ExamResultResponse> {
    const response = await api.get<ExamResultResponse>(`/exams/${examId}/results/${resultId}`)
    return response.data
  }

  /**
   * Get exam attempt history for a specific employee (Admin only)
   */
  async getEmployeeAttemptHistory(userId: number, filters?: ExamResultFilters): Promise<ExamAttemptHistoryResponse> {
    const params = new URLSearchParams()
    
    if (filters?.exam_id) params.append('exam_id', filters.exam_id.toString())
    if (filters?.lesson_id) params.append('lesson_id', filters.lesson_id.toString())
    if (filters?.date_from) params.append('reset_date_from', filters.date_from)
    if (filters?.date_to) params.append('reset_date_to', filters.date_to)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())
    if (filters?.page) params.append('page', filters.page.toString())

    const queryString = params.toString()
    const url = queryString 
      ? `/exams/employee/${userId}/attempt-history?${queryString}` 
      : `/exams/employee/${userId}/attempt-history`
    
    const response = await api.get<ExamAttemptHistoryResponse>(url)
    return response.data
  }

  /**
   * Get all exam attempt history (Admin only - all employees)
   */
  async getAllAttemptHistory(filters?: ExamResultFilters): Promise<ExamAttemptHistoryResponse> {
    const params = new URLSearchParams()
    
    if (filters?.employee_id) params.append('user_id', filters.employee_id.toString())
    if (filters?.exam_id) params.append('exam_id', filters.exam_id.toString())
    if (filters?.lesson_id) params.append('lesson_id', filters.lesson_id.toString())
    if (filters?.date_from) params.append('reset_date_from', filters.date_from)
    if (filters?.date_to) params.append('reset_date_to', filters.date_to)
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())
    if (filters?.page) params.append('page', filters.page.toString())

    const queryString = params.toString()
    const url = queryString ? `/exams/all-attempt-history?${queryString}` : '/exams/all-attempt-history'
    
    const response = await api.get<ExamAttemptHistoryResponse>(url)
    return response.data
  }
}

export const examResultService = new ExamResultService()
export default examResultService
